import {
  AlertTriangle, ArrowRightLeft, Building2, Car, CheckCircle, ClipboardCheck, Gavel, Pencil, Plus,
  ScanLine, ShieldCheck, Trash2, User, Warehouse, Wrench, XCircle, Activity, Undo2,
} from 'lucide-react'
import type {
  DefectDecision, EquipmentCondition, EquipmentLocationType, EquipmentStatus, WarrantyState,
} from '@/types/equipment'

export const STATUS_CONFIG: Record<EquipmentStatus, { label: string; className: string }> = {
  verfuegbar: { label: 'Verfügbar', className: 'bg-green-100 text-green-800 border-green-200' },
  im_einsatz: { label: 'Im Einsatz', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  defekt: { label: 'Defekt – Entscheidung offen', className: 'bg-red-100 text-red-800 border-red-200' },
  reparatur: { label: 'In Reparatur', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  garantie: { label: 'Garantiefall', className: 'bg-violet-100 text-violet-800 border-violet-200' },
  aussonderung_beantragt: { label: 'Aussonderung beantragt', className: 'bg-rose-100 text-rose-800 border-rose-300' },
  ausgemustert: { label: 'Ausgemustert', className: 'bg-gray-100 text-gray-600 border-gray-200' },
}
export const STATUS_KEYS = Object.keys(STATUS_CONFIG) as EquipmentStatus[]

export const CONDITION_CONFIG: Record<EquipmentCondition, { label: string; dot: string }> = {
  gut: { label: 'Gut', dot: 'bg-green-500' },
  gebrauchsspuren: { label: 'Gebrauchsspuren', dot: 'bg-lime-500' },
  eingeschraenkt: { label: 'Eingeschränkt', dot: 'bg-amber-500' },
  defekt: { label: 'Defekt', dot: 'bg-red-500' },
}
export const CONDITION_KEYS = Object.keys(CONDITION_CONFIG) as EquipmentCondition[]

export const LOCATION_CONFIG: Record<EquipmentLocationType, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  lager: { label: 'Lager', icon: Warehouse },
  projekt: { label: 'Baustelle', icon: Building2 },
  mitarbeiter: { label: 'Mitarbeiter', icon: User },
  fahrzeug: { label: 'Fahrzeug', icon: Car },
}
export const LOCATION_KEYS = Object.keys(LOCATION_CONFIG) as EquipmentLocationType[]

export const DECISION_CONFIG: Record<DefectDecision, { label: string; short: string; className: string; hint: string }> = {
  garantie: {
    label: 'Garantie – ins Büro', short: 'Garantie',
    className: 'bg-violet-100 text-violet-800 border-violet-200',
    hint: 'Nicht selbst öffnen, Gerät ins Büro, Büro wickelt mit Händler/Hersteller ab.',
  },
  reparatur: {
    label: 'Selbst reparieren', short: 'Reparatur',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
    hint: 'Reparatur in Eigenregie, Kosten beim Abschluss erfassen.',
  },
  entsorgen: {
    label: 'Entsorgen', short: 'Entsorgen',
    className: 'bg-rose-100 text-rose-800 border-rose-200',
    hint: 'Wird erst mit Bestätigung durch das Büro ausgemustert.',
  },
}
export const DECISION_KEYS = Object.keys(DECISION_CONFIG) as DefectDecision[]

export const WARRANTY_CONFIG: Record<WarrantyState, { label: string; className: string }> = {
  aktiv: { label: 'Garantie', className: 'bg-green-100 text-green-800 border-green-200' },
  laeuft_ab: { label: 'Garantie läuft ab', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  vermutlich: { label: 'Gewährl. (vermutl.)', className: 'bg-sky-100 text-sky-800 border-sky-200' },
  abgelaufen: { label: 'Keine Garantie', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  unbekannt: { label: 'Garantie unbekannt', className: 'bg-gray-50 text-gray-400 border-gray-200' },
}

export const HISTORY_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string; color: string }> = {
  erstellt: { icon: Plus, label: 'Angelegt', color: 'text-green-600' },
  bearbeitet: { icon: Pencil, label: 'Bearbeitet', color: 'text-gray-500' },
  bewegt: { icon: ArrowRightLeft, label: 'Umgebucht', color: 'text-blue-600' },
  gescannt: { icon: ScanLine, label: 'Gescannt', color: 'text-sky-600' },
  zustand: { icon: Activity, label: 'Zustand geändert', color: 'text-lime-700' },
  defekt_gemeldet: { icon: AlertTriangle, label: 'Defekt gemeldet', color: 'text-red-600' },
  entschieden: { icon: Gavel, label: 'Entscheidung', color: 'text-violet-600' },
  aussonderung_abgelehnt: { icon: Undo2, label: 'Aussonderung abgelehnt', color: 'text-amber-600' },
  repariert: { icon: CheckCircle, label: 'Defekt abgeschlossen', color: 'text-green-600' },
  ausgemustert: { icon: Trash2, label: 'Ausgemustert', color: 'text-rose-600' },
  dguv_geprueft: { icon: ClipboardCheck, label: 'DGUV-Prüfung', color: 'text-indigo-600' },
  // Altbestand (vor v2)
  zugewiesen: { icon: Building2, label: 'Zugewiesen', color: 'text-blue-600' },
  zurueckgegeben: { icon: Warehouse, label: 'Zurückgegeben', color: 'text-purple-600' },
  reparatur: { icon: Wrench, label: 'Reparatur gemeldet', color: 'text-amber-600' },
  garantie: { icon: ShieldCheck, label: 'Garantie', color: 'text-violet-600' },
  abgelehnt: { icon: XCircle, label: 'Abgelehnt', color: 'text-gray-500' },
}

export function formatIsoDate(iso: string | null | undefined) {
  if (!iso) return '–'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  // Zeitstempel (mit Uhrzeit) lokal formatieren, reine Daten nicht durch Date schicken (Zeitzonen-Kipp)
  if (iso.length > 10) {
    const d = new Date(iso.includes('T') || iso.endsWith('Z') ? iso : iso.replace(' ', 'T') + 'Z')
    if (!isNaN(d.getTime())) return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }
  return `${m[3]}.${m[2]}.${m[1]}`
}

export function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '–'
  const d = new Date(iso.includes('T') || iso.endsWith('Z') ? iso : iso.replace(' ', 'T') + 'Z')
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function formatEuro(n: number | null | undefined) {
  if (n == null) return '–'
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

/** Tag-Differenz-Text für DGUV/Garantie */
export function daysText(days: number | null | undefined) {
  if (days == null) return ''
  if (days < 0) return `seit ${-days} Tag${days === -1 ? '' : 'en'} überfällig`
  if (days === 0) return 'heute'
  return `in ${days} Tag${days === 1 ? '' : 'en'}`
}

/** Etikett-Einstellungen merken (pro Browser) */
const LABEL_KEY = 'compeer.equipment.labelOptions'
export function loadLabelOptions() {
  try {
    const raw = localStorage.getItem(LABEL_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* localStorage gesperrt → Default */ }
  return { tapeMm: 12, symbology: 'datamatrix', withName: true }
}
export function saveLabelOptions(opts: unknown) {
  try { localStorage.setItem(LABEL_KEY, JSON.stringify(opts)) } catch { /* egal */ }
}
